import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ms from "ms";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import useMyService from "../service/useMyService";

export function useMyHookGetAllEntities({ apiConfig }) {
  const {
    endpoint = "",
    queryKeyLabel = "",
    queryKeyId = null,
    params = {},
    enabledParam = true,
    retry = 2,
    staleTime = "1h",
  } = apiConfig;

  const { getAllEntities } = useMyService();

  const queryKey = useMemo(() => {
    const key = [queryKeyLabel];
    if (queryKeyId != null) {
      key.push(queryKeyId);
    }
    return key;
  }, [queryKeyLabel, queryKeyId]);

  const { data, error, isError, isSuccess, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: ({ signal }) =>
      getAllEntities({
        params,
        endpoint,
        signal,
      }),
    enabled: !!enabledParam,
    retry: retry !== 2 ? retry : 2,
    staleTime: staleTime === Infinity ? staleTime : ms(staleTime),
  });

  // Removed problematic useEffect that was invalidating queries on success,
  // which caused the query to run twice. Queries should only be invalidated
  // after mutations, not automatically on every successful fetch.

  useEffect(() => {
    if (error)
      toast.error(`An error occurred while fetching ${queryKeyLabel}.`);
  }, [error, queryKeyLabel]);

  return {
    data,
    isLoading,
    isError,
    error,
    isSuccess,
  };
}

export function useMyHookGetEntityById({ apiConfig }) {
  const {
    endpoint = "",
    queryKeyLabel = "",
    queryKeyId = "",
    enabledParam = null,
    retry = 2,
    staleTime = "0h",
    initialData = undefined,
    cacheTime = 0,
  } = apiConfig;

  const { getEntityById } = useMyService();

  const queryClient = useQueryClient();

  const queryKey = useMemo(() => {
    const output = [queryKeyLabel];
    if (queryKeyId != null) {
      output.push(queryKeyId);
    }
    return output;
  }, [queryKeyLabel, queryKeyId]);

  const { data, error, isError, isLoading, isSuccess } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }) =>
      getEntityById({ id: queryKeyId, endpoint, signal }),
    enabled: !!enabledParam,
    retry: retry !== 2 ? retry : 2,
    staleTime: staleTime === Infinity ? staleTime : ms(staleTime),
    initialData: initialData,
    // refetchOnWindowFocus: false,
    cacheTime,
  });

  useEffect(() => {
    if (error)
      toast.error(`An error occurred while fetching ${queryKeyLabel}.`);
  }, [error, queryKeyLabel]);

  useEffect(() => {
    if (initialData) {
      queryClient.invalidateQueries({ queryKey: queryKey });
    }
  }, [initialData, queryClient, queryKey]);

  return {
    data,
    isLoading,
    isError,
    isSuccess,
  };
}

export function useMyHookMutateEntity({ apiConfig }) {
  const {
    endpoint = "",
    queryKeyLabel = "",
    queryKeyId = "",
    enabledParam = true,
    retry = 2,
    staleTime = "1h",
    invalidateQueriesList = [],
    mutationType = "",
  } = apiConfig;

  const { addEntity, updateEntity, deleteEntity } = useMyService();

  const queryClient = useQueryClient();

  function mutationFn({ dataObj, id, signal }) {
    switch (mutationType) {
      case "ADD":
        return addEntity({
          dataObj,
          endpoint,
          signal,
        });
      case "UPDATE":
        return updateEntity({
          dataObj,
          endpoint,
          signal,
        });
      case "DELETE":
        return deleteEntity({
          id,
          endpoint,
          signal,
        });

      default:
        break;
    }
  }

  const { isSuccess, data, mutateAsync, isError, error, isPending } =
    useMutation({
      mutationFn: async ({ dataObj, id, signal }) =>
        mutationFn({
          dataObj,
          id,
          signal,
        }),
      enabled:
        !!enabledParam && ["ADD", "UPDATE", "DELETE"].includes(mutationType),
      retry,
      staleTime: ms(staleTime),
    });

  useEffect(() => {
    if (!isSuccess) return;
    if (queryKeyId) {
      queryClient.invalidateQueries({ queryKey: [queryKeyLabel, queryKeyId] });
    } else {
      queryClient.invalidateQueries({ queryKey: [queryKeyLabel] });
    }

    for (const qKey of invalidateQueriesList) {
      queryClient.invalidateQueries({ queryKey: [...qKey] });
    }
    toast.success("Operation successful.");
  }, [
    isSuccess,
    queryKeyId,
    queryClient,
    queryKeyLabel,
    invalidateQueriesList,
  ]);

  useEffect(() => {
    if (error)
      toast.error(`An error occurred while mutating ${queryKeyLabel}.`);
  }, [error, queryKeyLabel]);

  return {
    data,
    mutateAsync,
    isError,
    isPending,
  };
}
